import { UnreachableCaseError } from 'ts-essentials';

import type { GeospatialFeature } from '../types/geospatial-feature';
import type { FeatureGeometry } from '../types/geometry';

export interface ScenePoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface FeatureLayerRef {
  readonly id: string;
  readonly layerId: string;
}

export const FEATURE_MEMORY_BOUND_METERS = 100_000;
export const CAD_MIRROR_CULL_DISTANCE_METERS = 10_000;

const MIN_XY_COORDINATE_LENGTH = 2;
const LONGITUDE = 0;
const LATITUDE = 1;
const ELEVATION = 2;

export type LonLatProjector = (
  lon: number,
  lat: number
) => ScenePoint | undefined;

export function distanceXY(a: ScenePoint, b: ScenePoint): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function firstCoordinate(
  geometry: FeatureGeometry
): readonly [number, number, number?] | undefined {
  switch (geometry.type) {
    case 'Point':
      return geometry.coordinates;
    case 'LineString': {
      const first = geometry.coordinates[0];
      return first ? first : undefined;
    }
    case 'Polygon':
      return geometry.coordinates[0]?.[0];
    default:
      throw new UnreachableCaseError(geometry);
  }
}

export function projectFeatureSceneAnchor(
  feature: GeospatialFeature,
  project: LonLatProjector
): ScenePoint | undefined {
  const coordinate = firstCoordinate(feature.geometry);
  if (
    !coordinate ||
    coordinate.length < MIN_XY_COORDINATE_LENGTH ||
    !Number.isFinite(coordinate[LONGITUDE]) ||
    !Number.isFinite(coordinate[LATITUDE])
  ) {
    return undefined;
  }
  const projected = project(coordinate[LONGITUDE], coordinate[LATITUDE]);
  if (!projected) {
    return undefined;
  }
  const z =
    coordinate[ELEVATION] !== undefined &&
    Number.isFinite(coordinate[ELEVATION])
      ? coordinate[ELEVATION]
      : projected.z;
  return { x: projected.x, y: projected.y, z };
}

/** One live camera's claim on the shared feature store. */
export interface FeatureRetentionRegion {
  readonly reference: ScenePoint;
  readonly pinnedFeatureIds: ReadonlySet<string>;
}

// A feature survives if any region still wants it, so panes looking elsewhere
// never evict each other's nearby features. No regions means no basis to evict.
export function findFeatureIdsBeyondMemoryBound(
  sceneAnchors: Readonly<Record<string, ScenePoint>>,
  regions: readonly FeatureRetentionRegion[],
  memoryBoundMeters: number
): readonly string[] {
  if (regions.length === 0) {
    return [];
  }
  const evicted: string[] = [];
  for (const [featureId, anchor] of Object.entries(sceneAnchors)) {
    const retained = regions.some(
      (region) =>
        region.pinnedFeatureIds.has(featureId) ||
        distanceXY(region.reference, anchor) <= memoryBoundMeters
    );
    if (!retained) {
      evicted.push(featureId);
    }
  }
  return evicted;
}

export function selectCadMirroredFeatureIds(options: {
  features: readonly FeatureLayerRef[];
  sceneAnchors: Readonly<Record<string, ScenePoint>>;
  reference: ScenePoint;
  cullDistanceMeters: number;
  visibleLayerIds: ReadonlySet<string>;
  pinnedFeatureIds: ReadonlySet<string>;
}): readonly string[] {
  const mirrored: string[] = [];
  for (const feature of options.features) {
    if (!feature.layerId) {
      continue;
    }
    const pinned = options.pinnedFeatureIds.has(feature.id);
    if (!pinned && !options.visibleLayerIds.has(feature.layerId)) {
      continue;
    }
    const anchor = options.sceneAnchors[feature.id];
    if (!anchor) {
      continue;
    }
    if (
      !pinned &&
      distanceXY(options.reference, anchor) > options.cullDistanceMeters
    ) {
      continue;
    }
    mirrored.push(feature.id);
  }
  return mirrored;
}
